import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { BackendRoute } from '../../config/index';
import { QueryKey } from '../../constants/index';
import { ScenarioFundraisingCapTableRow } from '../../pages/scenario-modeling/scenario-cap-table/fundraising/fundraising.types';
import { useScenarioStore } from '../../pages/scenario-modeling/scenario-modeling.store';
import { ApiService } from '../../services/index';
import {
  LiquidityCapTableByStakeholderSeniority,
  ScenarioType,
} from '../../types/scenario-modeling.types';
import { DaysPerYearOption } from '../../types/share-classes.types';
import { useAppQuery } from '../use-app-query.hook';

export type FundraisingCapTableSummary = {
  avgSharePrice: number;
  avgSharePriceChange: number;
  companyPools: CapTablePool[];
  convertedBasis: number;
  convertedBasisChange: number;
  convertedBasisPercentage: number;
  convertedBasisPercentageChange: number;
  diluted: number;
  dilutedChange: number;
  dilutedPercentage: number;
  dilutedPercentageChange: number;
  equityPools: CapTablePool[];
  investment: number;
  investmentChange: number;
  issued: number;
  issuedChange: number;
  issuedPercentage: number;
  issuedPercentageChange: number;
  scenarioPools: CapTablePool[];
  voting: number;
  votingChange: number;
  votingPercentage: number;
  votingPercentageChange: number;
};
export type ParticipatingSettings = {
  capValue: number;
} & (
  | { daysPersYear: DaysPerYearOption; interest: number }
  | { daysPersYear?: never; interest?: never }
);
export interface LiquidityPreferences {
  multiple: number;
  participating?: ParticipatingSettings;
  seniority: number;
}

export type SeniorityInfoType = Record<
  number,
  {
    shareClasses: {
      id: string;
      liquidityPreferences: LiquidityPreferences;
      name: string;
    }[];
    stakeholders: {
      seniorityValue: number;
      shareClassId: string;
      stakeholderId: string;
    }[];
    totalCapParticipating: number;
    totalMultiple: number;
    totalSeniorityValue: number;
  }
>;

export type LiquidityCapTableSummary = {
  avgSharePrice: number;
  avgSharePriceChange: number;
  capParticipating: number;
  capParticipatingChange: number;
  commonStockProceeds: number;
  convertedBasis: number;
  convertedBasisChange: number;
  diluted: number;
  dilutedChange: number;
  dilutedPercentage: number;
  effectiveLiquidityProceeds: number;
  investment: number;
  investmentChange: number;
  issued: number;
  issuedChange: number;
  issuedPercentage: number;
  issuedPercentageChange: number;
  minLiquidityProceeds: number;
  multiple: number;
  multipleChange: number;
  participatingOnProceeds: number;
  proceedsWithoutLiquidity: number;
  seniorityInfo: LiquidityCapTableByStakeholderSeniority[];
  voting: number;
  votingChange: number;

  votingPercentage: number;
};

export type CapTablePool = {
  id: string;
  name: string;
  shareClassId: string;
} & Pick<
  ScenarioFundraisingCapTableRow,
  | 'convertedBasis'
  | 'convertedBasisPercentage'
  | 'defaultShareClass'
  | 'diluted'
  | 'dilutedPercentage'
  | 'issued'
  | 'issuedPercentage'
  | 'shareClasses'
  | 'unvested'
  | 'unvestedPercentage'
>;

export const useCapTableSummarize = <T extends ScenarioType = ScenarioType.FUNDRAISING>({
  enabled = true,
}: { enabled?: boolean } = {}) => {
  const queryClient = useQueryClient();
  const { scenarioId } = useScenarioStore();

  const { data, isLoading, isSuccess } = useAppQuery<
    T extends ScenarioType.FUNDRAISING ? FundraisingCapTableSummary : LiquidityCapTableSummary
  >({
    queryKey: [QueryKey.CAP_TABLE_SUMMARIZE, scenarioId],
    queryFn: () =>
      ApiService.get({
        endpoint: BackendRoute.SCENARIOS,
        routePath: [scenarioId, 'cap-table', 'summarize'],
      }),
    refetchOnWindowFocus: false,
    // Tab remounts must render cached totals instantly — see use-cap-table-stakeholder-by-all.
    staleTime: 30_000,
    enabled: !!scenarioId && enabled,
  });

  const invalidateQuery = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [QueryKey.CAP_TABLE_SUMMARIZE] });
  }, [queryClient]);

  return {
    invalidateQuery,
    data,
    isSuccess,
    isLoading,
  };
};
